import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { calendarPrintOptionDefaults } from '@strength/db-schema';
import { PrintHeaderSize, PrintOrientation } from '@strength/domain';
import { MdExpandMore } from '@teamworksdev/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import { InputLabel } from '../../../../components/input/input-label';
import { LabeledRadioChip } from '../../../../components/input/labeled-radio-chip';
import { cn } from '../../../../components/utils';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { usePlanContext } from '../../context/PlanContext';
import { getEffectiveTrackColor } from '../../timeline/utils';
import { useCalendarContext } from '../context/CalendarContext';
import {
  deriveHubEventTypesFromEvents,
  useCalendarEvents,
} from '../hooks/useCalendarEvents';
import { useGenerateCalendarPDF } from '../print/useGenerateCalendarPDF';

export type CalendarPrintDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const printDefaults = calendarPrintOptionDefaults();
const HEADER_TEXT_MAX_LENGTH = 100;

/**
 * Print/export drawer for the Annual Planner Calendar (SC-4068).
 *
 * Collects print options (orientation, fit-to-page, header text/size) and a
 * read-only legend of the tracks currently visible on the calendar, then
 * hands off to `useGenerateCalendarPDF` to kick off the print job and open
 * the resulting PDF once it's ready.
 */
export function CalendarPrintDrawer({
  open,
  onClose,
}: CalendarPrintDrawerProps) {
  const { t } = useTranslation();
  const {
    palette: {
      products: { performance },
    },
  } = useTheme();
  const { tracks, filteredTracks } = usePlanContext();
  const { selectedTrackIds, deselectedHubEventTypeIds, currentDate } =
    useCalendarContext();
  const { hubConnected } = useViewer();
  const { hubEventsOnCalendarFlag } = useFlags();
  const { generateCalendarPDF, pdfGenerating } = useGenerateCalendarPDF();

  const [orientation, setOrientation] = useState<PrintOrientation>(
    printDefaults.orientation,
  );
  const [fitToPage, setFitToPage] = useState<boolean>(printDefaults.fitToPage);
  const [headerText, setHeaderText] = useState<string | null>(
    printDefaults.headerText,
  );
  const [headerSize, setHeaderSize] = useState<PrintHeaderSize>(
    printDefaults.headerSize,
  );

  const headerTextTooLong = (headerText?.length ?? 0) > HEADER_TEXT_MAX_LENGTH;

  const tracksById = useMemo(
    () => new Map(tracks.map((track) => [track.id, track])),
    [tracks],
  );

  const activeTracks = useMemo(
    () => filteredTracks.filter((track) => selectedTrackIds.includes(track.id)),
    [filteredTracks, selectedTrackIds],
  );

  // Match the print worker's Metacal ±1 day buffer so timezone-ahead
  // boundary events (and their types) aren't missing from the legend while
  // still appearing on the PDF. Local-date bucketing in the worker keeps
  // only in-month events on the printed grid.
  const { data: hubData } = useCalendarEvents({
    startDate: currentDate.startOf('month').minus({ days: 1 }).toISO(),
    endDate: currentDate.endOf('month').plus({ days: 1 }).toISO(),
  });

  // Event-type summaries don't carry color (see `MetacalEventTypeSummary`);
  // build a color lookup from the raw events, which do have `event_type.color`.
  const hubEventTypeColorsById = useMemo(() => {
    const colors = new Map<string, string | null>();
    for (const event of hubData?.events ?? []) {
      const eventType = event['event_type'] as
        | { id?: string; color?: string | null }
        | null
        | undefined;
      if (eventType?.id && !colors.has(eventType.id)) {
        colors.set(eventType.id, eventType.color ?? null);
      }
    }
    return colors;
  }, [hubData?.events]);

  const activeHubEventTypes = useMemo(() => {
    const fromApi = hubData?.eventTypes ?? [];
    const eventTypes =
      fromApi.length > 0
        ? fromApi
        : deriveHubEventTypesFromEvents(
            (hubData?.events ?? []) as Parameters<
              typeof deriveHubEventTypesFromEvents
            >[0],
          );

    return eventTypes
      .filter((eventType) => !deselectedHubEventTypeIds.has(eventType.id))
      .map((eventType) => ({
        ...eventType,
        color: hubEventTypeColorsById.get(eventType.id) ?? null,
      }));
  }, [
    hubData?.eventTypes,
    hubData?.events,
    deselectedHubEventTypeIds,
    hubEventTypeColorsById,
  ]);

  // Same gate as the on-screen Hub events filter (CalendarFilter): only show
  // the legend when Hub is connected, the flag is on, and there's at least
  // one hub-event type currently visible on the calendar.
  const showHubEventsLegend =
    hubConnected && hubEventsOnCalendarFlag && activeHubEventTypes.length > 0;

  const handlePrint = () => {
    generateCalendarPDF({
      orientation,
      fitToPage,
      headerText,
      headerSize,
      trackIds: activeTracks.map((track) => track.id),
    });
  };

  return (
    <Drawer open={open} onClose={onClose} data-testid="calendar-print-drawer">
      <DrawerHeader title={t('print')} onClose={onClose} />
      <section
        role="region"
        aria-label={t('printOptions')}
        tabIndex={0}
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-6 pb-6 pt-2"
      >
        <Box>
          <InputLabel label={t('pageOrientation')} />
          <RadioGroup
            row
            name="orientation"
            value={orientation}
            onChange={(e) => setOrientation(e.target.value as PrintOrientation)}
            sx={{ gap: 1 }}
          >
            <LabeledRadioChip
              key="landscape"
              value="landscape"
              label={t('landscape')}
            />
            <LabeledRadioChip
              key="portrait"
              value="portrait"
              label={t('portrait')}
            />
          </RadioGroup>
        </Box>

        <Box className="flex items-center">
          <Switch
            size="small"
            checked={fitToPage}
            onChange={(e) => setFitToPage(e.target.checked)}
            inputProps={{ 'aria-label': t('fitToPage') }}
          />
          <Typography variant="body2" className="ml-2">
            {t('fitToPage')}
          </Typography>
        </Box>

        <div className="flex flex-col">
          <TextField
            label={t('headerText')}
            multiline
            maxRows={5}
            minRows={3}
            value={headerText ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setHeaderText(value.length > 0 ? value : null);
            }}
            error={headerTextTooLong}
            inputProps={{ maxLength: HEADER_TEXT_MAX_LENGTH }}
          />
          <FormHelperText
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mx: 0,
              mt: 1,
              color: headerTextTooLong ? 'red' : 'inherit',
            }}
          >
            <span>{t('headerTextHelperText')}</span>
            <span>
              {headerText?.length ?? 0}/{HEADER_TEXT_MAX_LENGTH}
            </span>
          </FormHelperText>
        </div>

        <Box>
          <InputLabel label={t('headerSize')} />
          <RadioGroup
            row
            name="headerSize"
            value={headerSize}
            onChange={(e) => setHeaderSize(e.target.value as PrintHeaderSize)}
            sx={{ gap: 1 }}
          >
            <LabeledRadioChip
              key="normal"
              value="normal"
              label={t('default')}
            />
            <LabeledRadioChip key="large" value="large" label={t('large')} />
          </RadioGroup>
        </Box>

        <Divider />

        <details open className="group [&::-webkit-details-marker]:hidden">
          <summary className="flex list-none cursor-pointer items-center justify-between">
            <InputLabel label={t('tracks')} />
            <MdExpandMore
              size={16}
              aria-hidden="true"
              className={cn('transition-transform', 'group-open:rotate-180')}
            />
          </summary>
          {activeTracks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" className="mt-1">
              {t('noTracksSelected')}
            </Typography>
          ) : (
            <ul className="m-0 mt-1 flex list-none flex-col gap-2 p-0">
              {activeTracks.map((track) => (
                <li key={track.id} className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    data-testid="track-color-swatch"
                    className="inline-block h-2 w-2 shrink-0 rounded-[3px]"
                    style={{
                      backgroundColor:
                        getEffectiveTrackColor(track, tracksById) ??
                        performance.primary,
                    }}
                  />
                  <Typography variant="body2" color="text.primary">
                    {track.name}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
        </details>

        {showHubEventsLegend && (
          <>
            <Divider />

            <details open className="group [&::-webkit-details-marker]:hidden">
              <summary className="flex list-none cursor-pointer items-center justify-between">
                <InputLabel label={t('hubEvents')} />
                <MdExpandMore
                  size={16}
                  aria-hidden="true"
                  className={cn(
                    'transition-transform',
                    'group-open:rotate-180',
                  )}
                />
              </summary>
              <ul className="m-0 mt-1 flex list-none flex-col gap-2 p-0">
                {activeHubEventTypes.map((eventType) => (
                  <li key={eventType.id} className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      data-testid="hub-event-color-swatch"
                      className="inline-block h-2 w-2 shrink-0 rounded-[3px]"
                      style={{
                        backgroundColor: eventType.color ?? performance.primary,
                      }}
                    />
                    <Typography variant="body2" color="text.primary">
                      {eventType.name}
                    </Typography>
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}
      </section>

      <DrawerFooter className="justify-end flex flex-row gap-2 w-full p-6">
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button
          disabled={headerTextTooLong || pdfGenerating}
          onClick={handlePrint}
          startIcon={
            pdfGenerating ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t('print')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
